import { Box } from '@wix/design-system';
import { useTranslation } from '@wix/fe-essentials/react';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import React, { useCallback, useRef } from 'react';
import { cancelHeadingStructureAIAtom, headingStructureAILoadingPageIdAtom } from '../atoms/heading-structure-ai-atoms';
import { isWaitingForBmLatestScanAtom, scrollerNodeRefAtom, unsavedChangedHandlersAtom } from '../atoms/session-atoms';
import { BiCtaNames } from '../bi/types';
import { SharedAltTextScreenWrapper } from '../components/fixers/alt-image-fixer/drill-in/alt-text-screen/shared-alt-text-screen-wrapper';
import { showSaveBeforeCloseModal } from '../components/modals/show-save-before-leave-modal';
import { showStopAIHeadingStructureModal } from '../components/modals/show-stop-ai-heading-structure-modal';
import { VerticalsDrillInScreen } from '../components/verticals-drill-in/verticals-drill-in-screen';
import { PanelLoader } from '../components/wizard-content/panel-loader';
import { useA11yWizardAdapter } from '../hooks/use-a11y-wizard-adapter';
import { useBiLogger } from '../hooks/use-bi-logger';
import { useMultilingualWarning } from '../hooks/use-multilingual-warning';
import { usePanelCloseHandler } from '../hooks/use-panel-close-handler';
import { useResetUnsavedChangesHandlers } from '../hooks/use-reset-unsaved-changes-handlers';
import { SecondaryLanguageWarning } from '../warnings/secondary-language-error';
import { CardsIssueList } from './cards-issue-list';
export const A11yWizardStandalonePanel = _ref => {
  let {
    showAltTextScreen,
    altTextScreenPageId,
    verticalsDrillInPageId,
    isFirstFetching,
    renderEmptyState,
    renderWelcomeScreen,
    renderFooter
  } = _ref;
  const {
    t
  } = useTranslation();
  const {
    editorHost,
    a11yWizardAdapter
  } = useA11yWizardAdapter();
  const biLogger = useBiLogger();
  const {
    shouldShowSecondaryLanguageWarning
  } = useMultilingualWarning();
  const {
    resetUnsavedChangesHandlers
  } = useResetUnsavedChangesHandlers();
  const [unsavedChangedHandlers, setUnsavedChangedHandlers] = useAtom(unsavedChangedHandlersAtom);
  const aiLoadingPageId = useAtomValue(headingStructureAILoadingPageIdAtom);
  const isWaitingForBmLatestScan = useAtomValue(isWaitingForBmLatestScanAtom);
  const showPanelLoader = isFirstFetching || isWaitingForBmLatestScan;
  const cancelHeadingStructureAI = useSetAtom(cancelHeadingStructureAIAtom);
  const nodeRef = useRef(null);
  const setNodeRef = useSetAtom(scrollerNodeRefAtom);
  setNodeRef(nodeRef);
  const unsavedChangedHandlersProps = {
    navigationGuard: aiLoadingPageId ? {
      exists: true,
      onNavigationAttempt: proceed => showStopAIHeadingStructureModal({
        a11yWizardAdapter,
        editorHost,
        handleContinue: () => {},
        handleStop: () => {
          cancelHeadingStructureAI(aiLoadingPageId);
          proceed();
        },
        t
      })
    } : undefined,
    setUnsavedChanges: (exists, apply, discard) => setUnsavedChangedHandlers({
      apply,
      discard,
      exists
    }),
    showSaveBeforeLeaveModal: (handleApply, handleDiscard) => showSaveBeforeCloseModal({
      a11yWizardAdapter,
      biLogger,
      editorHost,
      handleApply,
      handleDiscard,
      t
    }),
    unsavedChanges: unsavedChangedHandlers ? unsavedChangedHandlers : {
      apply: () => Promise.resolve(),
      exists: false
    }
  };
  const handleCloseAttempt = useCallback(() => {
    biLogger.ctaClick({
      ctaName: BiCtaNames.CloseTheWizard
    });
  }, [biLogger]);
  const handleCloseWithModal = useCallback(closePanelDirectly => {
    if (showAltTextScreen || verticalsDrillInPageId) {
      return false;
    }
    if (aiLoadingPageId) {
      showStopAIHeadingStructureModal({
        a11yWizardAdapter,
        editorHost,
        handleContinue: () => {},
        handleStop: () => {
          cancelHeadingStructureAI(aiLoadingPageId);
          closePanelDirectly();
        },
        t
      });
      return true;
    }
    if (unsavedChangedHandlers != null && unsavedChangedHandlers.exists) {
      showSaveBeforeCloseModal({
        a11yWizardAdapter,
        biLogger,
        editorHost,
        handleApply: () => {
          unsavedChangedHandlers.apply();
          resetUnsavedChangesHandlers();
        },
        handleDiscard: () => {
          unsavedChangedHandlers.discard == null || unsavedChangedHandlers.discard();
          resetUnsavedChangesHandlers();
          closePanelDirectly();
        },
        t
      });
      return true;
    }
    return false;
  }, [showAltTextScreen, verticalsDrillInPageId, aiLoadingPageId, unsavedChangedHandlers, a11yWizardAdapter, biLogger, editorHost, cancelHeadingStructureAI, t, resetUnsavedChangesHandlers]);
  usePanelCloseHandler({
    handleCloseWithModal,
    onCloseAttemptBI: handleCloseAttempt
  });
  return /*#__PURE__*/React.createElement(Box, {
    direction: "vertical",
    display: "flex",
    height: "100%",
    overflow: "hidden",
    verticalAlign: "space-between"
  }, showAltTextScreen ? /*#__PURE__*/React.createElement(Box, {
    direction: "vertical",
    height: "100%",
    overflow: "hidden",
    width: "100%"
  }, /*#__PURE__*/React.createElement(Box, {
    flex: "1 1 auto",
    minHeight: 0,
    overflow: "hidden",
    position: "relative",
    width: "100%"
  }, /*#__PURE__*/React.createElement(SharedAltTextScreenWrapper, {
    itemType: altTextScreenPageId.itemType,
    pageId: altTextScreenPageId.pageId
  }))) : verticalsDrillInPageId ? /*#__PURE__*/React.createElement(Box, {
    direction: "vertical",
    height: "100%",
    overflow: "hidden",
    width: "100%"
  }, /*#__PURE__*/React.createElement(Box, {
    flex: "1 1 auto",
    minHeight: 0,
    overflow: "hidden",
    position: "relative",
    width: "100%"
  }, /*#__PURE__*/React.createElement(VerticalsDrillInScreen, null))) : showPanelLoader ? /*#__PURE__*/React.createElement(PanelLoader, null) : /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Box, {
    direction: "vertical",
    flex: "1 1 auto",
    minHeight: 0,
    overflow: "auto",
    ref: nodeRef
  }, renderEmptyState ? renderWelcomeScreen : shouldShowSecondaryLanguageWarning ? /*#__PURE__*/React.createElement(Box, {
    align: "center",
    boxSizing: "border-box",
    direction: "vertical",
    gap: "SP1",
    height: "100%",
    minHeight: "600px",
    padding: "SP2 SP4 SP4 SP4"
  }, /*#__PURE__*/React.createElement(Box, {
    align: "center",
    margin: "SP2 0 28px 0"
  }, /*#__PURE__*/React.createElement(SecondaryLanguageWarning, null))) : /*#__PURE__*/React.createElement(Box, {
    direction: "vertical",
    height: "100%",
    minHeight: "600px"
  }, /*#__PURE__*/React.createElement(CardsIssueList, {
    unsavedChangesHandlers: unsavedChangedHandlersProps
  }))), /*#__PURE__*/React.createElement(Box, null, renderFooter)));
};
//# sourceMappingURL=a11y-wizard-standalone-panel.js.map